/**
 * Lightweight in-memory cache with TTL support.
 * Serves frequently-read data (journal stats, user preferences) from memory
 * and refreshes silently in the background.
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

class MemoryCache {
  private store = new Map<string, CacheEntry<unknown>>();

  /** Store a value with a TTL in milliseconds. */
  set<T>(key: string, value: T, ttlMs: number): void {
    this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }

  /** Retrieve a value. Returns undefined if missing or expired. */
  get<T>(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return undefined;
    }
    return entry.value as T;
  }

  /** Check if a key exists and is still valid. */
  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  /** Invalidate a specific key. */
  invalidate(key: string): void {
    this.store.delete(key);
  }

  /** Invalidate all keys matching a prefix. */
  invalidatePrefix(prefix: string): void {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) this.store.delete(key);
    }
  }

  /** Clear the entire cache. */
  clear(): void {
    this.store.clear();
  }
}

// Singleton — shared across the app
export const appCache = new MemoryCache();

// ─── TTL constants ────────────────────────────────────────────────────────────
export const TTL = {
  /** Journal stats: 30 seconds — refreshed on every write */
  JOURNAL_STATS: 30_000,
  /** Journal entries list: 10 seconds */
  JOURNAL_ENTRIES: 10_000,
  /** Ledger entries: 15 seconds */
  LEDGER_ENTRIES: 15_000,
  /** User preferences: 60 seconds */
  USER_PREFS: 60_000,
  /** Signal status counts: 20 seconds */
  SIGNAL_COUNTS: 20_000,
} as const;

// ─── Cache key builders ───────────────────────────────────────────────────────
export const cacheKey = {
  journalStats: (userId: string) => `journal:stats:${userId}`,
  journalEntries: (userId: string) => `journal:entries:${userId}`,
  ledgerEntries: (userId: string) => `ledger:entries:${userId}`,
  userPrefs: (userId: string, module: string) => `prefs:${userId}:${module}`,
  signalCounts: (userId: string) => `signals:counts:${userId}`,
};
